import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sso_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sso_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// API Methods
export const authAPI = {
  validateToken: (token) => api.post('/auth/validate', {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getMe: () => api.get('/auth/me'),
};

export const assetsAPI = {
  list: (params) => api.get('/assets', { params }),
  get: (assetId) => api.get(`/assets/${assetId}`),
  getPriceHistory: (assetId) => api.get(`/assets/${assetId}/price-history`),
  getSparkline: (assetId) => api.get(`/assets/${assetId}/sparkline`),
};

export const requestsAPI = {
  create: (data) => api.post('/requests', data),
  list: (params) => api.get('/requests', { params }),
};

export const portfolioAPI = {
  get: () => api.get('/portfolio'),
};

export const coinsAPI = {
  getBalance: () => api.get('/coins/balance'),
  getTransactions: () => api.get('/coins/transactions'),
};

export const dividendsAPI = {
  getMy: () => api.get('/dividends'),
};

export const marketplaceAPI = {
  getListings: (params) => api.get('/marketplace', { params }),
  createListing: (data) => api.post('/marketplace/listings', data),
  getMyListings: () => api.get('/marketplace/my-listings'),
  cancelListing: (listingId) => api.delete(`/marketplace/listings/${listingId}`),
  createOffer: (data) => api.post('/marketplace/offers', data),
  respondToOffer: (offerId, data) => api.put(`/marketplace/offers/${offerId}`, data),
};

export const documentsAPI = {
  upload: (data) => api.post('/documents', data),
  getByAsset: (assetId) => api.get(`/documents/${assetId}`),
  delete: (docId) => api.delete(`/admin/documents/${docId}`),
};

export const adminAPI = {
  // Assets
  createAsset: (data) => api.post('/admin/assets', data),
  updateAsset: (assetId, data) => api.put(`/admin/assets/${assetId}`, data),
  deleteAsset: (assetId) => api.delete(`/admin/assets/${assetId}`),
  
  // Requests
  listRequests: (params) => api.get('/admin/requests', { params }),
  updateRequest: (requestId, data) => api.put(`/admin/requests/${requestId}`, data),
  
  // Ownership
  getOwnership: (params) => api.get('/admin/ownership', { params }),
  
  // Audit
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Coins
  grantCoins: (userId, amount, description) => api.post('/admin/coins/grant', null, {
    params: { user_id: userId, amount, description }
  }),
  
  // Dividends
  distributeDividend: (data) => api.post('/admin/dividends', data),
  getDividends: (params) => api.get('/admin/dividends', { params }),
  
  // Documents
  deleteDocument: (docId) => api.delete(`/admin/documents/${docId}`),
  
  // Performance & Pricing
  addPerformance: (data) => api.post('/admin/performance', data),
  getPerformance: (assetId, limit = 12) => api.get(`/admin/performance/${assetId}`, { params: { limit } }),
  getPricingConfig: (assetId) => api.get(`/admin/pricing-config/${assetId}`),
  updatePricingConfig: (assetId, data) => api.put(`/admin/pricing-config/${assetId}`, data),
  overridePrice: (assetId, data) => api.post(`/admin/price-override/${assetId}`, data),
  
  // Database Management
  resetDatabase: () => api.post('/admin/reset-database'),
};

export const performanceAPI = {
  getSummary: (assetId) => api.get(`/performance/${assetId}/summary`),
};